import adicionarGraficoImg from '@/assets/help/laudo-adicionar-grafico.png';
import adicionarTabelaImg from '@/assets/help/laudo-adicionar-tabela.png';
import diagnosticoCidImg from '@/assets/help/laudo-diagnostico-cid.png';
import laudoEditorImg from '@/assets/help/laudo-editor.png';
import iaDemandaImg from '@/assets/help/laudo-ia-demanda.png';
import laudoModelosImg from '@/assets/help/laudo-modelos.png';
import personalizarImg from '@/assets/help/laudo-personalizar.png';
import resultadosImg from '@/assets/help/laudo-resultados.png';
import type { HelpArticle } from '../types';

export const REPORTS_ARTICLES: HelpArticle[] = [
    {
        slug: 'conhecendo-o-editor',
        topicId: 'reports',
        title: 'Conheça o editor de laudos',
        description:
            'As três áreas da tela de laudo: as etapas à esquerda, o documento no centro e o painel de apoio à direita.',
        updatedAt: '2026-07-03',
        keywords: ['editor', 'tela do laudo', 'etapas', 'preview', 'documento', 'painel'],
        blocks: [
            {
                type: 'paragraph',
                text: 'O editor de laudos é a principal tela do LaudoPsi. Ele foi desenhado para você escrever olhando para o documento final, sem surpresas na hora de exportar.',
            },
            {
                type: 'image',
                src: laudoEditorImg,
                alt: 'Editor de laudos com as etapas à esquerda, o documento no centro e o painel de apoio à direita',
            },
            { type: 'subheading', id: 'etapas', text: 'À esquerda: as etapas do laudo' },
            {
                type: 'paragraph',
                text: 'O menu lateral lista as seções do documento, de **Informações do Paciente** até **Recomendações**. Cada etapa mostra se está concluída ou a fazer, então você sempre sabe o que falta. Pode navegar em qualquer ordem.',
            },
            { type: 'subheading', id: 'documento', text: 'No centro: o documento em tempo real' },
            {
                type: 'paragraph',
                text: 'O centro da tela mostra uma página A4 com o laudo exatamente como ele vai sair no arquivo final. Você pode clicar direto no texto para editar, arrastar blocos e ajustar o zoom na barra inferior.',
            },
            { type: 'subheading', id: 'painel', text: 'À direita: o painel de apoio' },
            {
                type: 'paragraph',
                text: 'O painel da direita muda conforme a etapa ativa. Na etapa do paciente ele traz os campos de identificação. Na demanda, a geração com IA. Nos resultados, os blocos de tabela e gráfico. É o seu assistente de contexto em cada seção.',
            },
            { type: 'subheading', id: 'barra-superior', text: 'A barra superior' },
            {
                type: 'list',
                items: [
                    '**Salvar**: guarda o progresso do laudo.',
                    '**Comentários**: anotações por seção para você não se perder entre uma sessão de escrita e outra.',
                    '**Personalizar**: cores, fontes, cabeçalho e rodapé do documento.',
                    '**Template**: salve a estrutura atual ou aplique um template existente.',
                    '**Concluir laudo**: finaliza e exporta o documento em DOCX.',
                ],
            },
            {
                type: 'callout',
                tone: 'success',
                text: 'O editor tem corretor gramatical embutido e histórico de alterações. Escreva sem medo, dá para desfazer.',
            },
        ],
    },
    {
        slug: 'secoes-do-laudo',
        topicId: 'reports',
        title: 'As seções do laudo, passo a passo',
        description:
            'O que preencher em cada etapa, da descrição da demanda às recomendações, e os atalhos de cada uma.',
        updatedAt: '2026-07-03',
        keywords: [
            'seções',
            'demanda',
            'procedimentos',
            'observação do comportamento',
            'história clínica',
            'recomendações',
            'seção customizada',
        ],
        blocks: [
            {
                type: 'paragraph',
                text: 'O laudo do LaudoPsi segue a estrutura esperada para documentos psicológicos, com as seções que você já conhece da prática clínica. Este guia resume o papel de cada uma e os atalhos disponíveis no painel da direita.',
            },
            { type: 'subheading', id: 'paciente', text: '1. Informações do Paciente' },
            {
                type: 'paragraph',
                text: 'Selecione ou crie o paciente e informe o período da avaliação. Com as datas preenchidas, a **idade no exame** é calculada em anos, meses e dias, e o cabeçalho do documento é montado automaticamente.',
            },
            { type: 'subheading', id: 'demanda', text: '2. Descrição da Demanda' },
            {
                type: 'paragraph',
                text: 'Registre o motivo do encaminhamento. Se você enviou a anamnese do paciente para a plataforma, a IA usa esse material para sugerir um rascunho da seção.',
            },
            { type: 'subheading', id: 'procedimentos', text: '3. Procedimentos' },
            {
                type: 'paragraph',
                text: 'Descreva a metodologia e informe a quantidade de sessões realizadas. Há **modelos prontos** para acelerar a escrita, e os instrumentos inseridos em Análise são citados aqui automaticamente.',
            },
            { type: 'subheading', id: 'observacao', text: '4. Observação do Comportamento' },
            {
                type: 'paragraph',
                text: 'Você pode escrever livremente, partir de **modelos prontos** ou usar o **questionário rápido**, um checklist guiado sobre o estado do paciente que vira um texto clínico coeso em poucos minutos.',
            },
            { type: 'subheading', id: 'historia', text: '5. História Clínica' },
            {
                type: 'paragraph',
                text: 'A narrativa do desenvolvimento e do histórico do paciente. A IA pode gerar um rascunho combinando a anamnese enviada e o que você já preencheu nas seções anteriores.',
            },
            { type: 'subheading', id: 'resultados', text: '6. Análise' },
            {
                type: 'paragraph',
                text: 'A bateria de testes do laudo, com tabelas e gráficos. Este é o coração do documento e tem um guia próprio, **Adicione tabelas e resultados de testes ao laudo**.',
            },
            { type: 'subheading', id: 'diagnostico', text: '7. Diagnóstico e Conclusão' },
            {
                type: 'paragraph',
                text: 'Selecione as hipóteses diagnósticas com a busca de CID e gere a justificativa clínica com base na história e nos resultados. O diagnóstico é sempre seu, a IA apenas organiza o rascunho.',
            },
            { type: 'subheading', id: 'recomendacoes', text: '8. Recomendações' },
            {
                type: 'paragraph',
                text: 'O plano de ação: encaminhamentos padrão e estratégias personalizadas. A IA combina idade do paciente, história clínica e conclusão para propor recomendações técnicas que você revisa e ajusta.',
            },
            { type: 'subheading', id: 'customizadas', text: 'Seções customizadas' },
            {
                type: 'paragraph',
                text: 'Precisa de uma seção como "Entrevista com os pais"? Use o botão de adicionar seção no fim da lista de etapas. Todas as seções podem ser renomeadas e reordenadas. Esse recurso depende do seu plano.',
            },
        ],
    },
    {
        slug: 'gerar-rascunhos-com-ia',
        topicId: 'reports',
        title: 'Use a IA para gerar rascunhos das seções',
        description:
            'Como funciona o botão Gerar com IA, o que alimenta cada seção e como os créditos são consumidos.',
        updatedAt: '2026-07-03',
        keywords: [
            'ia',
            'inteligência artificial',
            'gerar com ia',
            'rascunho',
            'créditos',
            'anamnese',
        ],
        blocks: [
            {
                type: 'paragraph',
                text: 'A IA do LaudoPsi não escreve o laudo sozinha. Ela gera rascunhos técnicos a partir dos dados clínicos que você forneceu, para você revisar, ajustar e assinar embaixo. A palavra final é sempre sua.',
            },
            {
                type: 'image',
                src: iaDemandaImg,
                alt: 'Painel de geração com IA na seção Descrição da Demanda, mostrando os insumos da seção e quem precisa agir em cada um',
            },
            { type: 'subheading', id: 'como-funciona', text: 'Como gerar um rascunho' },
            {
                type: 'steps',
                items: [
                    'Abra a etapa desejada, como **Descrição da Demanda** ou **História Clínica**.',
                    'No painel da direita, confira a lista **Para gerar esta seção**. Os itens que travam a geração aparecem coloridos e dizem quem resolve — **Você preenche**, **Você envia** ou **Com o paciente** — com o atalho para resolver ali mesmo.',
                    'Itens em cinza não travam nada: **Opcional** é um dado que enriquece o texto se você quiser, e **Já coberto** é uma alternativa que outra fonte da lista já resolveu.',
                    'Comece pelos itens do topo: a lista coloca primeiro o que está travando a geração.',
                    'Clique em **Gerar com IA** e revise o texto sugerido direto no documento.',
                ],
            },
            { type: 'subheading', id: 'insumos', text: 'De onde vêm os dados' },
            {
                type: 'paragraph',
                text: 'Cada seção usa insumos diferentes. A demanda parte da anamnese enviada. A história clínica combina anamnese e seções anteriores. O diagnóstico usa a história clínica, os resultados dos testes e os CIDs que você selecionou. As recomendações usam a história, a conclusão e a idade do paciente.',
            },
            {
                type: 'callout',
                tone: 'info',
                text: 'Quanto melhor o insumo, melhor o rascunho. Enviar a anamnese do paciente logo no início da avaliação destrava a IA em praticamente todas as seções.',
            },
            { type: 'subheading', id: 'creditos', text: 'Créditos de IA' },
            {
                type: 'paragraph',
                text: 'Cada geração consome créditos de IA do seu plano. Melhorias rápidas de texto custam menos créditos que gerações completas de seção. Você acompanha o consumo e o saldo na página **Assinatura**.',
            },
            {
                type: 'callout',
                tone: 'success',
                text: 'Seus dados estão protegidos. Antes de qualquer texto ir para a IA, as informações que identificam o paciente são removidas. Veja o guia IA e sigilo profissional na seção Privacidade e LGPD.',
            },
        ],
    },
    {
        slug: 'resultados-dos-testes',
        topicId: 'reports',
        title: 'Adicione tabelas e resultados de testes ao laudo',
        description:
            'Três formas de montar as tabelas: digitar, colar do Excel ou escolher um teste do catálogo. E como importar resultados de editoras.',
        updatedAt: '2026-07-03',
        keywords: [
            'resultados',
            'tabela',
            'testes',
            'satepsi',
            'excel',
            'importar pdf',
            'editoras',
            'escores',
        ],
        blocks: [
            {
                type: 'paragraph',
                text: 'A etapa **Análise** concentra a bateria de testes do laudo. O painel da direita mostra de onde os resultados podem vir: **Adicionar instrumento**, **Enviar instrumentos ao paciente** e **Importar resultado de editora**. Ao selecionar um bloco que já está no documento (**Ctrl** ou **⌘** + clique), o painel passa a oferecer também os blocos de **Tabela**, **Gráfico**, **Texto** e **Imagem** para aquele instrumento — o nome dele aparece no topo do painel.',
            },
            {
                type: 'image',
                src: resultadosImg,
                alt: 'Etapa Análise com os blocos disponíveis no painel da direita',
            },
            { type: 'subheading', id: 'adicionar-tabela', text: 'Adicionando uma tabela' },
            {
                type: 'steps',
                items: [
                    'Selecione o instrumento no documento com **Ctrl** (ou **⌘**) + clique — o nome dele aparece no topo do painel da direita, que passa a mostrar os blocos daquele instrumento.',
                    'Clique em **Tabela** ou arraste o bloco para o documento. Ele entra logo abaixo do instrumento aberto no painel.',
                    'Clique no bloco recém-criado para escolher o teste da tabela.',
                    'Busque o teste no catálogo e a tabela já vem formatada para ele, pronta para receber os escores.',
                ],
            },
            {
                type: 'image',
                src: adicionarTabelaImg,
                alt: 'Bloco de tabela adicionado ao documento, aguardando a seleção do teste',
            },
            { type: 'subheading', id: 'tres-formas', text: 'Três formas de preencher' },
            {
                type: 'list',
                items: [
                    '**Escolher do catálogo**: são centenas de testes catalogados, com situação no SATEPSI e faixa etária. A estrutura da tabela vem pronta.',
                    '**Copiar e colar**: cole tabelas direto do Excel ou do Google Docs, como indica a dica no próprio bloco.',
                    '**Digitar na mão**: monte uma tabela livre e preencha célula por célula.',
                ],
            },
            {
                type: 'subheading',
                id: 'importar-editoras',
                text: 'Importando resultados de editoras',
            },
            {
                type: 'paragraph',
                text: 'Corrigiu o teste na plataforma de uma editora, como Pearson, Vetor ou Hogrefe, e recebeu o resultado em PDF? Você pode importar esse PDF para o LaudoPsi. A gente extrai os dados das tabelas para você não ter que redigitar nada.',
            },
            {
                type: 'subheading',
                id: 'instrumentos-online',
                text: 'Resultados vindos de instrumentos online',
            },
            {
                type: 'paragraph',
                text: 'Se você enviou um instrumento para o paciente responder online, o resultado corrigido chega nesta etapa pela opção **Atribuir instrumentos ao paciente**. Basta revisar e aprovar. O guia completo está na seção Testes e formulários.',
            },
            {
                type: 'callout',
                tone: 'success',
                text: 'Os instrumentos que você adiciona aqui são citados automaticamente na seção Procedimentos. Menos uma coisa para lembrar.',
            },
        ],
    },
    {
        slug: 'graficos-no-laudo',
        topicId: 'reports',
        title: 'Crie gráficos a partir das tabelas',
        description:
            'Tipos de gráfico disponíveis, escolha de cores e recursos clínicos como faixas de referência e linhas de corte.',
        updatedAt: '2026-07-03',
        keywords: [
            'gráfico',
            'barras',
            'linhas',
            'radar',
            'curva normal',
            'faixas de referência',
            'linhas de corte',
            'cores',
        ],
        blocks: [
            {
                type: 'paragraph',
                text: 'Um bom gráfico comunica em segundos o que a tabela leva minutos para mostrar. No LaudoPsi, todo gráfico nasce de uma tabela que já está no laudo.',
            },
            { type: 'subheading', id: 'criando', text: 'Criando um gráfico' },
            {
                type: 'steps',
                items: [
                    'Na etapa **Análise**, selecione o instrumento no documento com **Ctrl** (ou **⌘**) + clique e depois clique em **Gráfico** no painel da direita.',
                    'Escolha a **tabela de origem** e as colunas dos eixos X e Y.',
                    'Selecione o tipo de gráfico na galeria.',
                    'Avance para **Estilo e ajustes** para personalizar cores e detalhes, e clique em **Adicionar gráfico**.',
                ],
            },
            {
                type: 'image',
                src: adicionarGraficoImg,
                alt: 'Modal Novo gráfico com a galeria de tipos e a seleção da tabela de origem',
            },
            { type: 'subheading', id: 'tipos', text: 'Tipos disponíveis' },
            {
                type: 'list',
                items: [
                    '**Barras** e **Linhas**: comparação direta de escores entre subtestes ou momentos.',
                    '**Radar**: perfil visual de múltiplos domínios de uma vez.',
                    '**Dispersão** e **Dispersão (XY)**: relação entre duas medidas.',
                    '**Curva normal**: posiciona o resultado do paciente na distribuição, com faixas de classificação.',
                    '**Medidor**, **Barra de nível** e **Classificação por linha**: leituras rápidas de nível para escalas com faixas.',
                ],
            },
            { type: 'subheading', id: 'clinico', text: 'Recursos clínicos' },
            {
                type: 'paragraph',
                text: 'Em **Estilo e ajustes** você encontra as **faixas de referência**, com nome, intervalo e cor, as **linhas de corte** e o controle da **escala do eixo Y**. São esses recursos que transformam um gráfico genérico em um gráfico de avaliação psicológica.',
            },
            {
                type: 'callout',
                tone: 'info',
                text: 'As cores das séries seguem por padrão as cores do seu laudo, então os gráficos já nascem combinando com o documento. Você pode trocar cada cor individualmente.',
            },
        ],
    },
    {
        slug: 'buscar-cid-no-diagnostico',
        topicId: 'reports',
        title: 'Busque CIDs na seção de diagnóstico',
        description:
            'Como encontrar e selecionar códigos da CID-10 e da CID-11 sem sair do editor.',
        updatedAt: '2026-07-03',
        keywords: ['cid', 'cid-10', 'cid-11', 'diagnóstico', 'hipótese diagnóstica', 'código'],
        blocks: [
            {
                type: 'paragraph',
                text: 'Chega de procurar código de CID em site externo. A etapa **Diagnóstico e Conclusão** tem uma busca embutida com os catálogos da CID-10 e da CID-11.',
            },
            { type: 'subheading', id: 'como-buscar', text: 'Como buscar e selecionar' },
            {
                type: 'steps',
                items: [
                    'Abra a etapa **Diagnóstico e Conclusão**.',
                    'No painel da direita, clique no campo **Busque pelo CID**.',
                    'Digite o código ou uma descrição, como "TDAH" ou "6A05". A busca entende sinônimos e termos do dia a dia.',
                    'Marque um ou mais códigos e clique em **Aplicar**.',
                ],
            },
            {
                type: 'image',
                src: diagnosticoCidImg,
                alt: 'Modal Adicionar CIDs com resultados da busca por TDAH',
            },
            { type: 'subheading', id: 'gerando', text: 'Gerando a justificativa' },
            {
                type: 'paragraph',
                text: 'Com os CIDs selecionados, o botão **Gerar com IA** monta um rascunho de conclusão fundamentado na história clínica e nos resultados dos testes do laudo. O CID é opcional. Se o caso não pede um código, você pode gerar a conclusão sem ele.',
            },
            {
                type: 'callout',
                tone: 'warning',
                text: 'A IA nunca decide o diagnóstico. Ela escreve a justificativa a partir dos CIDs que você escolheu e das evidências do caso. Revise o texto antes de concluir o laudo.',
            },
        ],
    },
    {
        slug: 'personalizar-o-laudo',
        topicId: 'reports',
        title: 'Personalize o visual do seu laudo',
        description:
            'Cabeçalho, logo, papel timbrado, fontes, cores e rodapé. Deixe o documento com a cara do seu consultório.',
        updatedAt: '2026-07-03',
        keywords: [
            'personalizar',
            'cabeçalho',
            'logo',
            'papel timbrado',
            'fonte',
            'cores',
            'rodapé',
            'identidade visual',
        ],
        blocks: [
            {
                type: 'paragraph',
                text: 'O botão **Personalizar**, na barra superior do editor, abre o painel de customização visual. As mudanças aparecem na hora no preview ao lado.',
            },
            {
                type: 'image',
                src: personalizarImg,
                alt: 'Painel Customize o seu laudo, com os grupos Sua marca, Cada página e Blocos de resultados',
            },
            {
                type: 'paragraph',
                text: 'O painel está organizado em três grupos: **Sua marca** (o que vale para o laudo inteiro), **Cada página** (o que se repete em todas elas) e **Blocos de resultados** (o que só aparece se você tiver esses blocos). Cada linha fechada mostra a configuração atual à direita.',
            },
            { type: 'subheading', id: 'cores', text: 'Cores' },
            {
                type: 'paragraph',
                text: 'Escolha uma paleta pronta — agrupada por família — ou defina as cores primária e secundária à mão. Logo abaixo, **Onde estas cores aparecem** lista tudo que elas pintam: títulos de seção, títulos de grupo, cabeçalho das tabelas, gráficos e capa. Os ajustes de cor dos títulos de grupo e das tabelas ficam nessa lista, e não mais nas seções deles.',
            },
            { type: 'subheading', id: 'tipografia', text: 'Tipografia' },
            {
                type: 'paragraph',
                text: 'Escolha entre fontes clássicas de documento, como Georgia, Garamond, Helvetica, Arial e Times New Roman.',
            },
            { type: 'subheading', id: 'logo-e-papel-timbrado', text: 'Logo e papel timbrado' },
            {
                type: 'paragraph',
                text: 'Envie a sua **logo**, ajuste o tamanho dela e, dependendo do plano, aplique um **papel timbrado** completo.',
            },
            { type: 'subheading', id: 'cabecalho', text: 'Cabeçalho' },
            {
                type: 'paragraph',
                text: 'Mostre ou esconda seu nome, o número do CRP e uma descrição personalizada, e escolha o alinhamento do topo da página.',
            },
            {
                type: 'subheading',
                id: 'tabelas-e-titulos-de-grupo',
                text: 'Tabelas e títulos de grupo',
            },
            {
                type: 'paragraph',
                text: 'Defina o alinhamento do texto das tabelas de testes. Para os **títulos de grupo** — aqueles que separam os instrumentos em Resultados dos Testes, como Atenção ou Memória — escolha alinhamento, estilo da fonte e se a linha abaixo aparece.',
            },
            { type: 'subheading', id: 'rodape', text: 'Rodapé' },
            {
                type: 'paragraph',
                text: 'Adicione telefone, nome, e-mail e Instagram, e ajuste o espaçamento do rodapé.',
            },
            {
                type: 'callout',
                tone: 'success',
                text: 'Gostou do resultado? Salve o laudo como template para que os próximos documentos já nasçam com essa identidade visual. Veja o guia sobre templates.',
            },
        ],
    },
    {
        slug: 'templates-de-laudo',
        topicId: 'reports',
        title: 'Trabalhe com templates de laudo',
        description:
            'Use os modelos do catálogo do LaudoPsi ou salve os seus próprios templates para não começar do zero.',
        updatedAt: '2026-07-03',
        keywords: [
            'template',
            'modelo',
            'catálogo',
            'salvar template',
            'reaproveitar',
            'estrutura',
        ],
        blocks: [
            {
                type: 'paragraph',
                text: 'Se os seus laudos costumam seguir uma mesma estrutura, os templates economizam um tempo enorme. Existem dois tipos: os modelos do catálogo do LaudoPsi e os templates que você mesmo salva.',
            },
            { type: 'subheading', id: 'ponto-de-partida', text: 'Escolhendo um ponto de partida' },
            {
                type: 'paragraph',
                text: 'Ao criar um laudo novo, a tela **Escolha um ponto de partida** mostra as opções: **Criar do zero**, com a estrutura padrão em branco, ou usar um modelo do catálogo, como o Modelo TDAH Adulto, que já vem com rascunho das seções e estrutura de testes.',
            },
            {
                type: 'image',
                src: laudoModelosImg,
                alt: 'Tela Escolha um ponto de partida com a opção Criar do zero e um modelo do catálogo',
            },
            { type: 'subheading', id: 'salvar', text: 'Salvando o seu próprio template' },
            {
                type: 'steps',
                items: [
                    'Monte o laudo com a estrutura que você quer reaproveitar: seções, textos base, tabelas e personalização visual.',
                    'Na barra superior, abra o menu **Template**.',
                    'Escolha **Salvar como template** e dê um nome fácil de reconhecer depois.',
                ],
            },
            {
                type: 'paragraph',
                text: 'Na próxima vez que criar um laudo, seu template aparece na tela de ponto de partida, com tudo salvo: conteúdo das seções, tabelas e configurações visuais.',
            },
            {
                type: 'callout',
                tone: 'info',
                text: 'A quantidade de templates próprios depende do seu plano. Consulte os limites na página Assinatura.',
            },
        ],
    },
    {
        slug: 'exportar-em-docx',
        topicId: 'reports',
        title: 'Concluir e exportar o laudo em DOCX',
        description:
            'Como finalizar o documento, o que muda depois de concluído e por que ele fica somente leitura.',
        updatedAt: '2026-07-03',
        keywords: ['exportar', 'docx', 'word', 'concluir laudo', 'download', 'somente leitura'],
        blocks: [
            {
                type: 'paragraph',
                text: 'Quando o laudo estiver revisado, o botão **Concluir laudo** gera o arquivo final em **DOCX**, o formato do Word. O documento sai idêntico ao preview do editor, com sua personalização, tabelas e gráficos.',
            },
            { type: 'subheading', id: 'antes-de-concluir', text: 'Antes de concluir' },
            {
                type: 'list',
                items: [
                    'Revise cada seção no preview central.',
                    'Confira se o seu nome e CRP estão preenchidos no **Perfil**.',
                    'Verifique os dados do paciente e o período da avaliação no cabeçalho.',
                ],
            },
            { type: 'subheading', id: 'concluindo', text: 'Concluindo' },
            {
                type: 'steps',
                items: [
                    'Clique em **Concluir laudo**, no canto superior direito do editor.',
                    'Confirme a conclusão e aguarde a geração do arquivo.',
                    'Baixe o DOCX. Ele também fica disponível no Painel, na opção **Baixar em .docx** do laudo.',
                ],
            },
            {
                type: 'callout',
                tone: 'warning',
                title: 'O laudo concluído fica somente leitura',
                text: 'Depois de exportado, o laudo não pode mais ser editado na plataforma. Isso protege a integridade do documento emitido. Se precisar de uma nova versão, crie um novo laudo, de preferência a partir de um template.',
            },
            { type: 'subheading', id: 'limites', text: 'Limites do plano' },
            {
                type: 'paragraph',
                text: 'Cada plano inclui uma quantidade de laudos por período. Você acompanha o uso na página **Assinatura**, na tabela de limites do plano.',
            },
        ],
    },
];
